import type { ComponentType, SVGProps } from "react"
import { useEffect, useRef, useState } from "react"
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core"
import {
	restrictToParentElement,
	restrictToVerticalAxis,
} from "@dnd-kit/modifiers"
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
	IconCheckboxCheckedOutline18 as CheckboxIcon,
	IconListCheckboxOutline18 as DropdownIcon,
	IconEnvelopeOutline18 as EnvelopeIcon,
	IconFormOutline18 as FormIcon,
	IconGripDotsVerticalOutline18 as GripIcon,
	IconLockOutline18 as LockIcon,
	IconTextInputOutline18 as LongTextIcon,
	IconPlusOutline18 as PlusIcon,
	IconInputFieldOutline18 as ShortTextIcon,
	IconTrashOutline18 as TrashIcon,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import type {
	ContactFormField,
	ContactFormFieldType,
} from "@productlane/lib/src/contact-form-fields"
import { cn } from "@productlane/lib/src/browser"
import {
	CONTACT_FORM_FIELD_TYPES,
	MAX_CONTACT_FORM_FIELDS,
	visibleContactFormFields,
} from "@productlane/lib/src/contact-form-fields"
import {
	Badge,
	Button,
	IconButton,
	Input,
	Select,
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
	Switch,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { DragEndEvent } from "@dnd-kit/core"
import { toast } from "@/components/toast"
import { captureError } from "@/lib/capture-error"
import { mutateOk, useZero } from "@/lib/zero"

type Icon = ComponentType<SVGProps<SVGSVGElement>>

const TYPE_META: Record<ContactFormFieldType, { label: string; Icon: Icon }> = {
	shortText: { label: "Short text", Icon: ShortTextIcon },
	longText: { label: "Long text", Icon: LongTextIcon },
	dropdown: { label: "Dropdown", Icon: DropdownIcon },
	checkbox: { label: "Checkbox", Icon: CheckboxIcon },
}

const TYPE_OPTIONS = CONTACT_FORM_FIELD_TYPES.map((value) => ({
	value,
	label: TYPE_META[value].label,
}))

const BUILT_IN_FIELDS = [
	{
		id: "email",
		label: "Email",
		hint: "Only for visitors who are not signed in",
		Icon: EnvelopeIcon,
	},
	{ id: "message", label: "Message", hint: "Long text", Icon: LongTextIcon },
]

const SAVE_DELAY_MS = 600

const CARD_CLASS =
	"rounded-lg bg-white ring-1 ring-gray-200 dark:bg-gray-850 dark:ring-gray-700/80"

export function formFieldsSummary(customCount: number): string {
	if (customCount === 0)
		return "Customize the contact form and add your own fields."
	return `Email, message, and ${customCount} custom ${customCount === 1 ? "field" : "fields"}.`
}

/** The widget skips the form in live chat and none, so the row only shows in contact form mode. */
export function showsFormFieldsRow(settings: {
	isLiveChatEnabled?: boolean | null
	isFeedbackEnabled?: boolean | null
}): boolean {
	return !settings.isLiveChatEnabled && !!settings.isFeedbackEnabled
}

/** Moves the dragged field to the slot of the field it was dropped on. */
export function moveField(
	fields: ContactFormField[],
	activeId: string,
	overId: string,
): ContactFormField[] {
	const from = fields.findIndex((f) => f.id === activeId)
	const to = fields.findIndex((f) => f.id === overId)
	if (from < 0 || to < 0 || from === to) return fields
	return arrayMove(fields, from, to)
}

/** Applies a type change, giving a new dropdown one empty option and keeping checkboxes optional. */
export function changeFieldType(
	field: ContactFormField,
	type: ContactFormFieldType,
): ContactFormField {
	return {
		...field,
		type,
		required: type === "checkbox" ? false : field.required,
		options:
			type === "dropdown"
				? field.options?.length
					? field.options
					: [""]
				: undefined,
	}
}

function FieldTypeIcon({ Icon }: { Icon: Icon }) {
	return (
		<span className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-gray-100 text-secondary dark:bg-gray-800 [&_svg]:h-4 [&_svg]:w-4">
			<Icon className="flex-none" />
		</span>
	)
}

function BuiltInFieldRow({
	label,
	hint,
	Icon,
}: (typeof BUILT_IN_FIELDS)[number]) {
	return (
		<div className={cn(CARD_CLASS, "flex items-center gap-2 py-2 pl-1.5 pr-2")}>
			<span className="h-6 w-4 flex-none" />
			<FieldTypeIcon Icon={Icon} />
			<div className="flex min-w-0 flex-1 flex-col">
				<span className="truncate text-sm font-medium text-primary">
					{label}
				</span>
				<span className="truncate text-xs text-tertiary">{hint}</span>
			</div>
			<Badge size="sm">Required</Badge>
			<span className="flex h-7 w-7 flex-none items-center justify-center text-tertiary [&_svg]:h-3.5 [&_svg]:w-3.5">
				<LockIcon className="flex-none" aria-label="Always included" />
			</span>
		</div>
	)
}

function CustomFieldRow({
	field,
	isOpen,
	onToggle,
	onChange,
	onDelete,
	onBlur,
}: {
	field: ContactFormField
	isOpen: boolean
	onToggle: () => void
	onChange: (field: ContactFormField, options?: { immediate?: boolean }) => void
	onDelete: () => void
	onBlur: () => void
}) {
	const {
		attributes,
		listeners,
		setNodeRef,
		setActivatorNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: field.id })
	const { Icon, label: typeLabel } = TYPE_META[field.type]
	const options = field.options ?? []

	return (
		<div
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			data-testid="contact-form-field"
			className={cn(
				CARD_CLASS,
				isOpen && "ring-gray-300 dark:ring-gray-600",
				isDragging && "relative z-10 shadow-sm",
			)}
		>
			<div className="flex items-center gap-2 py-2 pl-1.5 pr-2">
				<button
					type="button"
					ref={setActivatorNodeRef}
					className="flex h-6 w-4 flex-none touch-none items-center justify-center text-tertiary [&_svg]:h-3.5 [&_svg]:w-3.5"
					aria-label={`Reorder ${field.label || "field"}`}
					{...attributes}
					{...listeners}
				>
					<GripIcon className="flex-none" />
				</button>
				<FieldTypeIcon Icon={Icon} />
				<button
					type="button"
					onClick={onToggle}
					aria-expanded={isOpen}
					className="flex min-w-0 flex-1 flex-col items-start text-left"
				>
					<span
						className={cn(
							"w-full truncate text-sm font-medium",
							field.label.trim() ? "text-primary" : "text-tertiary",
						)}
					>
						{field.label.trim() || "Untitled field"}
					</span>
					<span className="w-full truncate text-xs text-tertiary">
						{typeLabel}
					</span>
				</button>
				{field.required && <Badge size="sm">Required</Badge>}
				<IconButton
					Icon={TrashIcon}
					variant="ghost"
					aria-label="Delete field"
					onClick={onDelete}
				/>
			</div>
			{isOpen && (
				<div className="space-y-4 border-t border-gray-200 px-4 py-4 dark:border-gray-700/80">
					<Input
						name={`${field.id}-label`}
						label="Label"
						value={field.label}
						maxLength={200}
						autoFocus={!field.label}
						onChange={(e) => onChange({ ...field, label: e.target.value })}
						onBlur={onBlur}
					/>
					<Select
						name={`${field.id}-type`}
						label="Type"
						value={field.type}
						options={TYPE_OPTIONS}
						onChange={(type) =>
							onChange(changeFieldType(field, type as ContactFormFieldType), {
								immediate: true,
							})
						}
					/>
					{field.type === "dropdown" && (
						<div className="space-y-1.5">
							<div className="text-sm font-medium text-primary">Options</div>
							{options.map((option, index) => (
								<div key={index} className="flex items-start gap-1.5">
									<Input
										name={`${field.id}-option-${index}`}
										className="flex-1"
										value={option}
										maxLength={200}
										onChange={(e) =>
											onChange({
												...field,
												options: options.map((o, i) =>
													i === index ? e.target.value : o,
												),
											})
										}
										onBlur={onBlur}
									/>
									<IconButton
										Icon={XIcon}
										variant="ghost"
										aria-label="Remove option"
										disabled={options.length === 1}
										onClick={() =>
											onChange(
												{
													...field,
													options: options.filter((_, i) => i !== index),
												},
												{ immediate: true },
											)
										}
									/>
								</div>
							))}
							<Button
								variant="ghost"
								size="sm"
								Icon={PlusIcon}
								onClick={() =>
									onChange(
										{ ...field, options: [...options, ""] },
										{ immediate: true },
									)
								}
							>
								Add option
							</Button>
						</div>
					)}
					{field.type !== "checkbox" && (
						<Switch
							name={`${field.id}-required`}
							label="Required"
							description="Customers can only send the form once it is filled."
							direction="text-first"
							checked={field.required}
							onCheckedChange={(required) =>
								onChange({ ...field, required }, { immediate: true })
							}
						/>
					)}
				</div>
			)}
		</div>
	)
}

function ContactFormFieldsSheet({
	open,
	onOpenChange,
	settingsId,
	storedFields,
	onLiveChange,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	settingsId: string
	storedFields: ContactFormField[]
	onLiveChange: (fields: ContactFormField[] | null) => void
}) {
	const zero = useZero()
	const [draft, setDraft] = useState(storedFields)
	const [openId, setOpenId] = useState<string | null>(null)
	const pending = useRef<ContactFormField[] | null>(null)
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

	// The drawer edits a copy; it reloads from Zero each time it opens.
	useEffect(() => {
		if (open) setDraft(storedFields)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	)

	const flush = async () => {
		clearTimeout(timer.current)
		const fields = pending.current
		pending.current = null
		if (!fields) return
		try {
			await mutateOk(
				zero.mutate(
					mutators.widgetSettings.updateContactFormFields({
						id: settingsId,
						fields,
					}),
				),
			)
		} catch (error) {
			captureError(error, {
				domain: "portal",
				action: "update contact form fields",
			})
			toast.error({ title: "Couldn't save the form fields" })
		}
	}

	const flushRef = useRef(flush)
	flushRef.current = flush
	useEffect(() => () => void flushRef.current(), [])

	const commit = (
		next: ContactFormField[],
		{ immediate = false }: { immediate?: boolean } = {},
	) => {
		setDraft(next)
		onLiveChange(next)
		pending.current = next
		clearTimeout(timer.current)
		if (immediate) void flush()
		else timer.current = setTimeout(() => void flush(), SAVE_DELAY_MS)
	}

	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		if (!over) return
		const next = moveField(draft, String(active.id), String(over.id))
		if (next !== draft) commit(next, { immediate: true })
	}

	return (
		<Sheet
			open={open}
			onOpenChange={(next) => {
				if (!next) {
					void flush()
					onLiveChange(null)
				}
				onOpenChange(next)
			}}
		>
			<SheetContent
				side="right"
				className="flex w-full flex-col gap-0 border-gray-200 p-0 sm:max-w-md dark:border-gray-700/80"
			>
				<SheetHeader className="border-b border-gray-200 px-5 py-4 dark:border-gray-700/80">
					<SheetTitle className="text-base">Contact form fields</SheetTitle>
					<SheetDescription>
						Ask for the details your team needs on every request.
					</SheetDescription>
				</SheetHeader>
				<div className="flex-1 space-y-2 overflow-y-auto px-5 pb-8 pt-4">
					{BUILT_IN_FIELDS.map((field) => (
						<BuiltInFieldRow key={field.id} {...field} />
					))}
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						modifiers={[restrictToVerticalAxis, restrictToParentElement]}
						onDragEnd={handleDragEnd}
					>
						<SortableContext
							items={draft.map((f) => f.id)}
							strategy={verticalListSortingStrategy}
						>
							<div className="space-y-2">
								{draft.map((field) => (
									<CustomFieldRow
										key={field.id}
										field={field}
										isOpen={openId === field.id}
										onToggle={() =>
											setOpenId(openId === field.id ? null : field.id)
										}
										onChange={(next, options) =>
											commit(
												draft.map((f) => (f.id === next.id ? next : f)),
												options,
											)
										}
										onDelete={() =>
											commit(
												draft.filter((f) => f.id !== field.id),
												{ immediate: true },
											)
										}
										onBlur={() => void flush()}
									/>
								))}
							</div>
						</SortableContext>
					</DndContext>
					{draft.length < MAX_CONTACT_FORM_FIELDS && (
						<Button
							variant="outline"
							Icon={PlusIcon}
							className="w-full max-w-none"
							onClick={() => {
								const id = crypto.randomUUID()
								commit(
									[
										...draft,
										{ id, label: "", type: "shortText", required: false },
									],
									{ immediate: true },
								)
								setOpenId(id)
							}}
						>
							Add field
						</Button>
					)}
				</div>
			</SheetContent>
		</Sheet>
	)
}

export function ContactFormFieldsRow({
	settingsId,
	storedFields,
	onLiveChange,
}: {
	settingsId: string
	storedFields: ContactFormField[]
	onLiveChange: (fields: ContactFormField[] | null) => void
}) {
	const [open, setOpen] = useState(false)
	return (
		<div className="flex items-center justify-between gap-4 py-3">
			<div className="min-w-0">
				<div className="text-sm font-medium text-primary">Form fields</div>
				<div className="mt-1 text-xs text-secondary">
					{formFieldsSummary(visibleContactFormFields(storedFields).length)}
				</div>
			</div>
			<Button variant="outline" Icon={FormIcon} onClick={() => setOpen(true)}>
				Customize fields
			</Button>
			<ContactFormFieldsSheet
				open={open}
				onOpenChange={setOpen}
				settingsId={settingsId}
				storedFields={storedFields}
				onLiveChange={onLiveChange}
			/>
		</div>
	)
}
